import type {
  PairDetail,
  PulseItem,
  SearchHit,
  SparklinePoint,
  TokenSummary,
  TradeSummary,
} from '../api/types'
import { candleTradePrice } from './candles'
import { isMainTokenRow, isNonTonQuotedTrade } from './discoverRowKey'
import { appendFiveMinuteEvent } from './fiveMinuteWindow'
import { tradeCarriesValuation } from './liveFeedGate'
import { advancePriceWindow, applyLiveTrade } from './repricePair'
import { mergeWindowTrades, tradeWindowUsdPrice, windowEventFromTrade } from './surfaceTrades'
import { isNonTonQuote, scaledValuation, tradeRowUsdRate, tradeUsdPrice } from './tonQuote'

type TokenSort = 'top' | 'trending' | 'surge' | 'new' | 'volume_24h' | 'txns_24h'

// countersOnly: the trade is older than one already applied for this token
// (out-of-order delivery) — accumulate volume/txns but never rewind the
// price-like fields a newer trade already set (REALTIME-CONSISTENCY).
export interface ApplyTradeOptions {
  countersOnly?: boolean
  priceOnly?: boolean
  preserveSeedSlowFields?: boolean
  exactPoolLiquidity?: boolean
}

export function applyTradeToTokenSummaries(
  rows: TokenSummary[],
  trade: TradeSummary,
  sort: TokenSort = 'volume_24h',
  opts: ApplyTradeOptions = {}
): TokenSummary[] {
  let changed = false
  const next = rows.map((row) => {
    if (!tradeMatchesTokenRow(row, trade)) return row
    changed = true
    return applyTradeToTokenSummary(row, trade, opts)
  })
  if (!changed) return rows
  return sortTokenRows(next, sort)
}

// applyTradeToTokenSummariesNoReorder patches the matching row's live values
// (price, mcap, liquidity, volume, txns, sparkline) before the caller applies
// the board's ranking policy.
export function applyTradeToTokenSummariesNoReorder(
  rows: TokenSummary[],
  trade: TradeSummary,
  opts: ApplyTradeOptions = {}
): TokenSummary[] {
  let changed = false
  const next = rows.map((row) => {
    if (!tradeMatchesTokenRow(row, trade)) return row
    changed = true
    return applyTradeToTokenSummary(row, trade, opts)
  })
  return changed ? next : rows
}

// Search rows are exact-pool rows. A trade for one pool must never overwrite
// the liquidity (or any other route metric) of a sibling pool sharing master.
// The price-window cursor is the seed/replay coverage barrier: buffered trades
// already represented by the authoritative seed are not counted twice.
export function applyTradeToSearchHits(rows: SearchHit[], trade: TradeSummary): SearchHit[] {
  let changed = false
  const next = rows.map((row) => {
    if (!row.pool_addr || row.pool_addr !== trade.pool_addr) return row
    changed = true
    return applyTradeToSearchHit(row, trade)
  })
  return changed ? next : rows
}

function applyTradeToSearchHit(row: SearchHit, trade: TradeSummary): SearchHit {
  const patched: SearchHit = { ...row }
  const price = candleTradePrice(trade)
  if (tradeIsNewerThanSearchPrice(row, trade)) {
    if (finiteNumber(trade.mcap_usd)) patched.mcap_usd = trade.mcap_usd
    if (finiteNumber(trade.liquidity_usd)) patched.liquidity_usd = trade.liquidity_usd
    if (row.price_window) {
      patched.price_window = advancePriceWindow(row.price_window, trade, price)
      patched.price_change_24h_pct = patched.price_window.pct
    }
  }
  const usd = tradeQuoteUsd(trade)
  if (usd != null) patched.volume_24h_usd = positiveOrZero(row.volume_24h_usd) + usd
  return patched
}

function tradeIsNewerThanSearchPrice(row: SearchHit, trade: TradeSummary): boolean {
  const sourceTS = row.price_window?.current_source_ts
  if (!sourceTS) return true
  const coveredTS = Date.parse(sourceTS) / 1000
  if (!Number.isFinite(coveredTS) || trade.ts !== coveredTS) return trade.ts > coveredTS
  const coveredLT = row.price_window?.current_source_lt
  if (typeof coveredLT !== 'number' || typeof trade.lt !== 'number') return false
  // An exactly-equal cursor is the covering trade itself. Its valued form
  // (stage-3 B1 patch — the pending frame advanced this cursor carrying no
  // USD) must still move the valuation; upstream identity dedupe admits it
  // exactly once.
  if (trade.lt === coveredLT) return tradeCarriesValuation(trade)
  return trade.lt > coveredLT
}

// rankTokensByVolume re-orders rows by window volume in USD at trade time
// (desc, stable) — the one basis rows of every quote share.
export function rankTokensByVolume(rows: TokenSummary[]): TokenSummary[] {
  return sortTokenRows(rows, 'volume_24h')
}

// --- stage-3 B1 valuation patches ------------------------------------------
//
// The fast lane publishes a trade's frame at MC-applied speed with
// mc_status:"pending" and no USD valuation; the valued frame follows with the
// SAME identity. The gate admits that second frame as `patch: true`, and these
// appliers add ONLY what the pending frame could not carry: the USD volume
// share, the valued window event (identity-replace heals the indexed USD
// windows), and — when the trade is still the token's newest — the price-like
// fields and the sparkline point. Counters (txns, native volume) were already
// applied and are never touched again.

export function applyValuationPatchToTokenSummaries(
  rows: TokenSummary[],
  trade: TradeSummary,
  newest: boolean,
  opts: ApplyTradeOptions = {}
): TokenSummary[] {
  let changed = false
  const next = rows.map((row) => {
    if (!tradeMatchesTokenRow(row, trade)) return row
    changed = true
    return applyValuationPatchToTokenSummary(row, trade, newest, opts)
  })
  return changed ? next : rows
}

function applyValuationPatchToTokenSummary(
  row: TokenSummary,
  trade: TradeSummary,
  newest: boolean,
  opts: ApplyTradeOptions
): TokenSummary {
  const next: TokenSummary = { ...row }
  if (newest) applyTradePriceFields(next, row, trade, opts)
  if (row.window_events_5m)
    next.window_events_5m = appendFiveMinuteEvent(row.window_events_5m, trade)
  if (row.window_events)
    next.window_events = mergeWindowTrades(row.window_events, [windowEventFromTrade(trade)])
  const usd = tradeQuoteUsd(trade)
  if (usd != null) {
    next.volume_window_usd = positiveOrZero(row.volume_window_usd) + usd
    next.vol_recent_usd = positiveOrZero(row.vol_recent_usd) + usd
  }
  return next
}

export function applyValuationPatchToPulseItems(
  rows: PulseItem[],
  trade: TradeSummary,
  newest: boolean
): PulseItem[] {
  let changed = false
  const next = rows.map((row) => {
    if (!tradeMatchesPulseItem(row, trade)) return row
    changed = true
    return applyValuationPatchToPulseItem(row, trade, newest)
  })
  return changed ? next : rows
}

function applyValuationPatchToPulseItem(
  row: PulseItem,
  trade: TradeSummary,
  newest: boolean
): PulseItem {
  const next: PulseItem = { ...row }
  if (row.window_events_ready)
    next.window_events = mergeWindowTrades(row.window_events ?? [], [windowEventFromTrade(trade)])
  if (newest) {
    if (finiteNumber(trade.mcap_usd)) next.mcap_usd = trade.mcap_usd
    if (finiteNumber(trade.fdv_usd)) next.fdv_usd = trade.fdv_usd
  }
  const usd = tradeQuoteUsd(trade)
  if (usd != null) next.volume_24h_usd = positiveOrZero(row.volume_24h_usd) + usd
  return next
}

export function applyTradeToPulseItems(
  rows: PulseItem[],
  trade: TradeSummary,
  opts: ApplyTradeOptions = {}
): PulseItem[] {
  let changed = false
  const next = rows.map((row) => {
    if (!tradeMatchesPulseItem(row, trade)) return row
    changed = true
    return applyTradeToPulseItem(row, trade, opts)
  })
  return changed ? next : rows
}

export function tradeMatchesPulseItem(row: PulseItem, trade: TradeSummary): boolean {
  const activePool =
    row.status === 'migrated' && row.migrated_pool_addr ? row.migrated_pool_addr : row.jetton_master
  return Boolean(activePool && trade.pool_addr === activePool)
}

export function applyTradeToPairDetailMap(
  current: Map<string, PairDetail | null>,
  trade: TradeSummary
): Map<string, PairDetail | null> {
  let changed = false
  const next = new Map<string, PairDetail | null>()
  for (const [key, detail] of current) {
    if (!detail || !tradeMatchesPairDetail(key, detail, trade)) {
      next.set(key, detail)
      continue
    }
    changed = true
    next.set(key, applyTradeToPairDetail(detail, trade))
  }
  return changed ? next : current
}

function applyTradeToTokenSummary(
  row: TokenSummary,
  trade: TradeSummary,
  opts: ApplyTradeOptions = {}
): TokenSummary {
  const next: TokenSummary = { ...row }
  if (!opts.countersOnly) applyTradePriceFields(next, row, trade, opts)
  if (row.window_events_5m)
    next.window_events_5m = appendFiveMinuteEvent(row.window_events_5m, trade)
  if (row.window_events)
    next.window_events = mergeWindowTrades(row.window_events, [windowEventFromTrade(trade)])
  const quote = positiveOrZero(trade.quote_amount)
  // quote_amount is the pool's own quote units — TON only on a TON-quoted
  // row. A pool row without TON keeps its native counters at the REST zero;
  // quote units must never land in a TON-labelled field.
  const tonRow = !isNonTonQuote(row.primary_quote_token)
  if (tonRow) next.volume_24h_native = positiveOrZero(row.volume_24h_native) + quote
  const usd = tradeQuoteUsd(trade)
  // A live trade IS a recent trade, so it grows the acceleration numerator too;
  // Trending's client-side re-rank picks it up immediately. No time-expiry here
  // (it self-heals on the next REST reload), but over-counting recent activity
  // is the correct direction for a "heating up now" signal. A pending frame
  // has no USD yet: its valued patch adds it (applyValuationPatchToTokenSummary).
  if (usd != null) {
    next.volume_window_usd = positiveOrZero(row.volume_window_usd) + usd
    next.vol_recent_usd = positiveOrZero(row.vol_recent_usd) + usd
  }
  if (tonRow) next.vol_recent_native = positiveOrZero(row.vol_recent_native) + quote
  next.txns_24h = positiveOrZero(row.txns_24h) + 1
  if (trade.side === 'buy') next.txns_24h_buys = positiveOrZero(row.txns_24h_buys) + 1
  if (trade.side === 'sell') next.txns_24h_sells = positiveOrZero(row.txns_24h_sells) + 1
  return next
}

// Price-like fields — skipped for countersOnly (out-of-order) trades.
function applyTradePriceFields(
  next: TokenSummary,
  row: TokenSummary,
  trade: TradeSummary,
  opts: ApplyTradeOptions
): void {
  const price = candleTradePrice(trade)
  if (finitePositive(price)) {
    next.last_price = price
  }
  // The sparkline series is USD (ohlcv_usd closes) — the live edge must append
  // the event's USD point, NEVER the native candle price: pushing ~0.56 TON
  // into a $1.00 USDT series drew a fake collapse to ~$800M mcap on hover. The
  // point is the stamped price_usd when the frame carries it, else the CANDLE
  // basis × the trade's own stamped rate for a non-TON quote (the tape's
  // usd_price twin). Unpriced quote / pending frame → no live point.
  const usdPoint = tradeWindowUsdPrice(trade, price)
  if (usdPoint != null) {
    next.sparkline = appendSparklinePoint(
      removeSyntheticAsOfAnchor(row.sparkline, row.price_window),
      trade.ts,
      usdPoint
    )
  }
  // A stamped price_usd is verbatim (execution × rate); a non-TON-quoted trade
  // has none — its own stamped ton_usd_rate prices it. Never TON/USD.
  const usdPrice = finiteNumber(trade.price_usd)
    ? (trade.price_usd as number)
    : tradeUsdPrice(trade)
  if (usdPrice != null) next.price_usd = usdPrice
  if (finiteNumber(trade.mcap_usd)) {
    next.mcap_usd = trade.mcap_usd
  } else {
    // A non-TON trade carries no mcap envelope: scale the seed valuation by the
    // live USD move off the row's PREVIOUS USD price — the scaledPairValuation
    // twin. TON trades never scale (the helper's quote gate).
    const scaled = scaledValuation(trade, usdPrice, row.price_usd, row.mcap_usd)
    if (scaled != null) next.mcap_usd = scaled
  }
  // The token path never wrote a stamped fdv_usd; a non-TON trade's scaled
  // valuation is still the honest move when the row has one to scale.
  const scaledFdv = scaledValuation(trade, usdPrice, row.price_usd, row.fdv_usd)
  if (scaledFdv != null) next.fdv_usd = scaledFdv
  if (
    finiteNumber(trade.liquidity_usd) &&
    (!opts.exactPoolLiquidity ||
      !row.primary_pool_addr ||
      row.primary_pool_addr === trade.pool_addr)
  ) {
    next.liquidity_usd = trade.liquidity_usd
  }
  if (finiteNumber(trade.bonding_progress_pct))
    next.bonding_progress_pct = trade.bonding_progress_pct
  if (row.price_window) {
    next.price_window = advancePriceWindow(row.price_window, trade, price)
    next.price_change_24h_pct = next.price_window.pct
  }
}

function removeSyntheticAsOfAnchor(
  series: SparklinePoint[] | undefined,
  window: TokenSummary['price_window']
): SparklinePoint[] | undefined {
  if (!series?.length || !window?.current_source_ts) return series
  const asOf = Math.floor(Date.parse(window.as_of) / 1000)
  const sourceTs = Math.floor(Date.parse(window.current_source_ts) / 1000)
  const last = series.at(-1)
  if (
    !Number.isFinite(asOf) ||
    !Number.isFinite(sourceTs) ||
    asOf <= sourceTs ||
    last?.ts !== asOf
  ) {
    return series
  }
  return series.slice(0, -1)
}

function applyTradeToPulseItem(
  row: PulseItem,
  trade: TradeSummary,
  opts: ApplyTradeOptions = {}
): PulseItem {
  const next: PulseItem = { ...row }
  if (row.window_events_ready)
    next.window_events = mergeWindowTrades(row.window_events ?? [], [windowEventFromTrade(trade)])
  if (!opts.countersOnly) {
    if (finiteNumber(trade.mcap_usd)) next.mcap_usd = trade.mcap_usd
    if (finiteNumber(trade.fdv_usd)) next.fdv_usd = trade.fdv_usd
    if (finiteNumber(trade.bonding_progress_pct)) {
      next.bonding_progress_pct = trade.bonding_progress_pct
      next.progress_known = true
    }
  }
  const quote = positiveOrZero(trade.quote_amount)
  next.volume_24h_native = positiveOrZero(row.volume_24h_native) + quote
  const usd = tradeQuoteUsd(trade)
  if (usd != null) next.volume_24h_usd = positiveOrZero(row.volume_24h_usd) + usd
  next.txns_24h = positiveOrZero(row.txns_24h) + 1
  if (trade.side === 'buy') next.txns_24h_buys = positiveOrZero(row.txns_24h_buys) + 1
  if (trade.side === 'sell') next.txns_24h_sells = positiveOrZero(row.txns_24h_sells) + 1
  return next
}

export function applyTradeToPairDetail(
  row: PairDetail,
  trade: TradeSummary,
  opts: ApplyTradeOptions = {}
): PairDetail {
  const price = candleTradePrice(trade)
  const next = opts.countersOnly ? { ...row } : applyLiveTrade(row, trade, price)
  if (row.window_events)
    next.window_events = mergeWindowTrades(row.window_events, [windowEventFromTrade(trade)])
  if (opts.preserveSeedSlowFields) {
    // A coverage-closed REST seed was fetched after this tail was classified.
    // Its independent LP/reserve state may be newer than the trade itself.
    next.liquidity_usd = row.liquidity_usd
    next.reserve_base_raw = row.reserve_base_raw
    next.reserve_quote_raw = row.reserve_quote_raw
    next.bonding_progress_pct = row.bonding_progress_pct
  }
  if (opts.priceOnly) return next
  const quote = positiveOrZero(trade.quote_amount)
  // Same quote-unit rule: the TON-labelled counter grows only on a TON-quoted
  // pair; quote units always land on the quote-labelled one.
  if (!isNonTonQuote(row.quote_token))
    next.volume_24h_native = positiveOrZero(row.volume_24h_native) + quote
  if (typeof row.volume_24h_quote === 'number')
    next.volume_24h_quote = positiveOrZero(row.volume_24h_quote) + quote
  const usd = tradeQuoteUsd(trade)
  if (usd != null && trade.side === 'buy' && typeof row.volume_24h_buy_usd === 'number') {
    next.volume_24h_buy_usd = positiveOrZero(row.volume_24h_buy_usd) + usd
  }
  if (usd != null && trade.side === 'sell' && typeof row.volume_24h_sell_usd === 'number') {
    next.volume_24h_sell_usd = positiveOrZero(row.volume_24h_sell_usd) + usd
  }
  const nowSec = Math.floor(Date.now() / 1000)
  if (trade.ts > nowSec - 60 * 60) applyPairSideWindow(next, row, trade, usd, '1h')
  if (trade.ts > nowSec - 6 * 60 * 60) applyPairSideWindow(next, row, trade, usd, '6h')
  next.txns_24h = positiveOrZero(row.txns_24h) + 1
  if (trade.side === 'buy') next.txns_24h_buys = positiveOrZero(row.txns_24h_buys) + 1
  if (trade.side === 'sell') next.txns_24h_sells = positiveOrZero(row.txns_24h_sells) + 1
  return next
}

function applyPairSideWindow(
  next: PairDetail,
  row: PairDetail,
  trade: TradeSummary,
  usd: number | null,
  window: '1h' | '6h'
): void {
  const buysKey = `txns_${window}_buys` as const
  const sellsKey = `txns_${window}_sells` as const
  const buyVolumeKey = `volume_${window}_buy_usd` as const
  const sellVolumeKey = `volume_${window}_sell_usd` as const
  if (trade.side === 'buy' && typeof row[buysKey] === 'number') {
    next[buysKey] = positiveOrZero(row[buysKey]) + 1
  }
  if (trade.side === 'sell' && typeof row[sellsKey] === 'number') {
    next[sellsKey] = positiveOrZero(row[sellsKey]) + 1
  }
  if (usd != null && trade.side === 'buy' && typeof row[buyVolumeKey] === 'number') {
    next[buyVolumeKey] = positiveOrZero(row[buyVolumeKey]) + usd
  }
  if (usd != null && trade.side === 'sell' && typeof row[sellVolumeKey] === 'number') {
    next[sellVolumeKey] = positiveOrZero(row[sellVolumeKey]) + usd
  }
}

// tradeMatchesTokenRow: a pool row takes only its own pool's trades; a token's
// main row takes the token's trades as before, but never a trade of a pool
// without TON — that pool is its own row (nothing is summed across pools).
export function tradeMatchesTokenRow(row: TokenSummary, trade: TradeSummary): boolean {
  if (!isMainTokenRow(row))
    return Boolean(row.primary_pool_addr) && row.primary_pool_addr === trade.pool_addr
  // Same rule as the backend's pending overlay: a trade of a pool without TON
  // reaches a main row only when that pool is the row's own primary pool.
  if (isNonTonQuotedTrade(trade)) return row.primary_pool_addr === trade.pool_addr
  return tradeMatchesToken(row.jetton_master, row.primary_pool_addr, trade)
}

export function tradeMatchesToken(
  master: string | undefined,
  pool: string | undefined,
  trade: TradeSummary
): boolean {
  return (
    (!!master && (master === trade.base_token || master === trade.pool_addr)) ||
    (!!pool && pool === trade.pool_addr)
  )
}

export function tradeMatchesPrimaryTokenRoute(
  master: string | undefined,
  primaryPool: string | undefined,
  trade: TradeSummary
): boolean {
  const route = primaryPool || master
  return Boolean(route && trade.pool_addr === route)
}

function tradeMatchesPairDetail(key: string, detail: PairDetail, trade: TradeSummary): boolean {
  return (
    key === trade.base_token ||
    key === trade.pool_addr ||
    detail.base_token === trade.base_token ||
    detail.pool_addr === trade.pool_addr
  )
}

function appendSparklinePoint(
  series: SparklinePoint[] | undefined,
  ts: number,
  price: number
): SparklinePoint[] {
  const base = Array.isArray(series) ? series : []
  const point = { ts, price }
  if (base.length > 0 && base[base.length - 1]?.ts === ts) {
    return [...base.slice(0, -1), point]
  }
  const next = [...base, point]
  if (next.length <= 96) return next
  // Preserve the backend's exact window/start boundary and the newest live tail.
  return [next[0] as SparklinePoint, ...next.slice(-95)]
}

function sortTokenRows(rows: TokenSummary[], sort: TokenSort): TokenSummary[] {
  // Only the raw-magnitude legacy sorts (volume_24h / txns_24h) can be cheaply
  // recomputed per-trade on the client. The Discover axes top/trending/surge are
  // liquidity-gated backend composites the client can't replicate from a running
  // total, and New is age-ordered — so those keep their authoritative REST order
  // and just let the patched values tick in place until the next discover.tick
  // reload. This avoids rows jumping into a WRONG order between reloads.
  if (sort !== 'volume_24h' && sort !== 'txns_24h') return rows
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const delta = tokenSortValue(b.row, sort) - tokenSortValue(a.row, sort)
      return delta || a.index - b.index
    })
    .map(({ row }) => row)
}

function tokenSortValue(row: TokenSummary, sort: TokenSort): number {
  if (sort === 'txns_24h') return positiveOrZero(row.txns_24h)
  return positiveOrZero(row.volume_window_usd)
}

// The trade's quote value in USD at its own time: quote × the trade's own
// quote→USD rate, in the one order every reader of a trade row shares
// (tonQuote.tradeRowUsdRate) — its stamped ton_usd_rate first, the rate the
// server's window volume uses. price_usd / price is only the legacy recovery
// of a TON-quoted row without a stamp: price_usd is stamped as the EXECUTION
// price × the quote's USD rate (trade_event_mcap.go), so that ratio is the
// rate; against the candle basis (spot_price) it is off by the dust
// correction, the same trap surfaceTrades.tradeUSDRate documents. A trade
// quoted outside TON has no such recovery — 0/absent stays null, never TON/USD.
function tradeQuoteUsd(trade: TradeSummary): number | null {
  // A pending frame carries no USD for ANY quote — its valued patch adds that
  // share (the stamped rate already rides the pending frame, so without this
  // guard the same USD would be counted twice).
  if (trade.mc_status === 'pending') return null
  const rate = tradeRowUsdRate(trade)
  if (rate == null) return null
  const quote = positiveOrZero(trade.quote_amount)
  if (quote <= 0) return 0
  return quote * rate
}

function finiteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v)
}

function finitePositive(v: unknown): v is number {
  return finiteNumber(v) && v > 0
}

function positiveOrZero(v: unknown): number {
  return finiteNumber(v) && v > 0 ? v : 0
}
